import * as React from 'react'

import { Controller, useFormContext, type RegisterOptions } from 'react-hook-form'

import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

import { useControlName } from './lib/use-control-name'

/**
 * Controles de escolha — caixa, interruptor e grupo de opções em linhas.
 *
 * **Alvo de toque.** É sempre o `<label>` inteiro, nunca a caixa. O primitivo
 * tem 16–20px e fica abaixo do piso de 44×44 que `ACCESSIBILITY.md` exige em
 * fluxo principal; é o rótulo que entrega a área real. `NetworkingConsent` já
 * tinha chegado a essa conclusão sozinho antes desta camada existir.
 *
 * **Dois modos.** Passando `value` e `onChange`, o controle opera controlado e
 * não toca no react-hook-form. Sem eles, registra-se pelo `name` — da prop ou
 * do `Field`. O modo controlado não é conveniência: `PartnerLeadForm` monta
 * perguntas dinâmicas com `useState`, e um componente de design system não pode
 * exigir uma biblioteca de formulário para ser usado.
 *
 * A separação em dois componentes internos existe porque a alternativa seria
 * chamar `useController` condicionalmente — o que as regras de hooks proíbem, e
 * que quebraria de todo jeito na ausência de `FormProvider`.
 */

type Registration<T> = {
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  value?: T
  onChange?: (value: T) => void
  rules?: RegisterOptions
  /**
   * Valor inicial quando o formulário não o declara. Existe porque o wrapper
   * legado o repassava ao `useController`, e sem ele um campo que nascia
   * marcado passaria a nascer vazio — mudando o payload em silêncio.
   */
  defaultValue?: T
  /** Injetados pelo `Field.Control`; repassados ao controle real. */
  id?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
}

function RegisteredChoice<T>({
  name: nameProp,
  rules,
  defaultValue,
  children
}: {
  name?: string
  rules?: RegisterOptions
  defaultValue?: T
  children: (field: {
    value: T
    onChange: (value: T) => void
    onBlur: () => void
    name: string
  }) => React.ReactElement
}) {
  const name = useControlName(nameProp)
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) =>
        children({
          value: field.value as T,
          onChange: field.onChange,
          onBlur: field.onBlur,
          name: field.name
        })
      }
    />
  )
}

const rowClasses =
  'flex min-h-11 cursor-pointer items-start gap-3 rounded-[var(--control-r-md)] ' +
  'transition-colors has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50'

// ─── Checkbox ───────────────────────────────────────────────────────────────

type ControlIdentity = {
  id?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
}

type CheckboxRowProps = ControlIdentity & {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  onBlur?: () => void
  label: React.ReactNode
  description?: React.ReactNode
  disabled?: boolean
  className?: string
}

function CheckboxRow({
  checked,
  onCheckedChange,
  onBlur,
  label,
  description,
  disabled,
  className,
  ...identity
}: CheckboxRowProps) {
  const descriptionId = React.useId()
  // A descrição interna e a que o `Field.Control` injeta precisam coexistir:
  // sobrescrever uma pela outra deixa o campo sem parte do que o leitor de tela
  // deveria anunciar.
  const describedBy =
    [description ? descriptionId : null, identity['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined

  return (
    <label className={cn(rowClasses, 'py-2', className)}>
      <Checkbox
        checked={checked}
        onCheckedChange={value => onCheckedChange(value === true)}
        onBlur={onBlur}
        disabled={disabled}
        className="mt-0.5 h-5 w-5 shrink-0"
        id={identity.id}
        aria-labelledby={identity['aria-labelledby']}
        aria-invalid={identity['aria-invalid']}
        aria-describedby={describedBy}
      />
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-base font-semibold text-gray-900">{label}</span>
        {description ? (
          <span id={descriptionId} className="text-sm text-gray-700">
            {description}
          </span>
        ) : null}
      </span>
    </label>
  )
}

type CheckboxFieldProps = Registration<boolean> &
  Omit<CheckboxRowProps, 'checked' | 'onCheckedChange' | 'onBlur'>

export function CheckboxField({
  name,
  value,
  onChange,
  rules,
  defaultValue,
  ...row
}: CheckboxFieldProps) {
  if (onChange) {
    return <CheckboxRow checked={!!value} onCheckedChange={onChange} {...row} />
  }

  return (
    <RegisteredChoice<boolean> name={name} rules={rules} defaultValue={defaultValue}>
      {field => (
        <CheckboxRow
          checked={!!field.value}
          onCheckedChange={field.onChange}
          onBlur={field.onBlur}
          {...row}
        />
      )}
    </RegisteredChoice>
  )
}

// ─── Switch ─────────────────────────────────────────────────────────────────

type SwitchRowProps = CheckboxRowProps

function SwitchRow({
  checked,
  onCheckedChange,
  onBlur,
  label,
  description,
  disabled,
  className,
  ...identity
}: SwitchRowProps) {
  const descriptionId = React.useId()
  const describedBy =
    [description ? descriptionId : null, identity['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined

  return (
    // O interruptor fica à direita: é o padrão de ajuste, em que o rótulo
    // explica e o controle mostra o estado no fim da linha.
    <label className={cn(rowClasses, 'items-center justify-between py-2', className)}>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-base font-semibold text-gray-900">{label}</span>
        {description ? (
          <span id={descriptionId} className="text-sm text-gray-700">
            {description}
          </span>
        ) : null}
      </span>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        onBlur={onBlur}
        disabled={disabled}
        className="shrink-0"
        id={identity.id}
        aria-labelledby={identity['aria-labelledby']}
        aria-invalid={identity['aria-invalid']}
        aria-describedby={describedBy}
      />
    </label>
  )
}

type SwitchFieldProps = Registration<boolean> &
  Omit<SwitchRowProps, 'checked' | 'onCheckedChange' | 'onBlur'>

export function SwitchField({
  name,
  value,
  onChange,
  rules,
  defaultValue,
  ...row
}: SwitchFieldProps) {
  if (onChange) {
    return <SwitchRow checked={!!value} onCheckedChange={onChange} {...row} />
  }

  return (
    <RegisteredChoice<boolean> name={name} rules={rules} defaultValue={defaultValue}>
      {field => (
        <SwitchRow
          checked={!!field.value}
          onCheckedChange={field.onChange}
          onBlur={field.onBlur}
          {...row}
        />
      )}
    </RegisteredChoice>
  )
}

// ─── Radio rows ─────────────────────────────────────────────────────────────

export type RadioRowOption = {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

type RadioRowGroupProps = ControlIdentity & {
  selected: string
  onSelect: (value: string) => void
  onBlur?: () => void
  groupName: string
  /** Rótulo do grupo. Vira a `legend` do `fieldset`. */
  legend?: React.ReactNode
  options: RadioRowOption[]
  disabled?: boolean
  className?: string
  /** Permite desmarcar clicando na opção já escolhida. */
  allowDeselect?: boolean
}

function RadioRowGroup({
  selected,
  onSelect,
  onBlur,
  groupName,
  legend,
  options,
  disabled,
  className,
  allowDeselect = false,
  ...identity
}: RadioRowGroupProps) {
  return (
    // A identidade injetada vai no `fieldset`: o grupo é o campo, e é ele que o
    // rótulo externo descreve — não cada opção.
    <fieldset
      className={cn('border-0 p-0', className)}
      aria-labelledby={identity['aria-labelledby']}
      aria-describedby={identity['aria-describedby']}
      aria-invalid={identity['aria-invalid']}
    >
      {legend ? (
        <legend className="mb-2 p-0 text-[0.9375rem] font-semibold text-gray-900">
          {legend}
        </legend>
      ) : null}
      <div className="flex flex-col gap-2">
        {options.map(option => {
          const isSelected = selected === option.value
          const isDisabled = disabled || option.disabled

          return (
            <label
              key={option.value}
              className={cn(
                'relative flex min-h-12 cursor-pointer items-center gap-2.5',
                'rounded-[var(--control-r-md)] border px-3 transition-colors',
                isSelected ? 'border-brand bg-brand-light/60' : 'border-input bg-white',
                isDisabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name={groupName}
                value={option.value}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => onSelect(option.value)}
                onClick={() => {
                  if (allowDeselect && isSelected) onSelect('')
                }}
                onBlur={onBlur}
              />
              {/* O marcador é desenhado porque o input está `sr-only`, e por
                  isso precisa cumprir MASTER §7 L3 na mão: contorno visível em
                  repouso, não só quando marcado. Um grupo cuja opção não
                  escolhida não tem contorno não comunica que é selecionável. */}
              <span
                className={cn(
                  'h-5 w-5 shrink-0 rounded-full bg-white',
                  isSelected ? 'border-[6px] border-brand' : 'border border-input'
                )}
                aria-hidden
              />
              <span className="text-[0.9375rem] text-gray-800">{option.label}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

type RadioRowFieldProps = Registration<string> &
  Omit<RadioRowGroupProps, 'selected' | 'onSelect' | 'onBlur' | 'groupName'> & {
    groupName?: string
  }

export function RadioRowField({
  name,
  value,
  onChange,
  rules,
  defaultValue,
  groupName,
  ...group
}: RadioRowFieldProps) {
  if (onChange) {
    return (
      <RadioRowGroup
        selected={value ?? ''}
        onSelect={onChange}
        groupName={groupName ?? name ?? 'radio-group'}
        {...group}
      />
    )
  }

  return (
    <RegisteredChoice<string> name={name} rules={rules} defaultValue={defaultValue}>
      {field => (
        <RadioRowGroup
          selected={field.value ?? ''}
          onSelect={field.onChange}
          onBlur={field.onBlur}
          groupName={groupName ?? field.name}
          {...group}
        />
      )}
    </RegisteredChoice>
  )
}

// Nomes explícitos para o React DevTools: sem eles a árvore mostra apenas
// `CheckboxRow`/`SwitchRow`/`RadioRowGroup`, e não o componente que o consumidor
// escreveu.
CheckboxField.displayName = 'CheckboxField'
SwitchField.displayName = 'SwitchField'
RadioRowField.displayName = 'RadioRowField'
